/**
 * Base Node Component
 *
 * Base component for all workflow nodes with common styling and handles.
 * Supports execution status visualization with visual indicators.
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import clsx from 'clsx';
import { CheckCircle2, XCircle, Loader2, Clock, SkipForward } from 'lucide-react';
import type { NodeData, NodeType, StepStatus } from '@/types/imageWorkflow';
import { useNodeExecutionStatus } from '../../hooks/useWorkflowStore';

// Node type colors and icons
export const nodeConfig: Record<
  NodeType,
  {
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ReactNode;
    label: string;
  }
> = {
  start: {
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
    label: 'Start',
  },
  prompt: {
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
        />
      </svg>
    ),
    label: 'Prompt',
  },
  generate: {
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
    label: 'Generate',
  },
  edit: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
    label: 'Edit',
  },
  fuse: {
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
        />
      </svg>
    ),
    label: 'Fuse',
  },
  lucky_charm: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    ),
    label: 'Lucky Charm',
  },
  push: {
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
    ),
    label: 'Push',
  },
  output: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    icon: (
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    ),
    label: 'Output',
  },
  read_today_artifact: {
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-300',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    label: 'Read Today Artifact',
  },
};

// Execution status configuration
const executionStatusConfig: Record<
  StepStatus,
  {
    borderColor: string;
    icon: React.ReactNode;
    animation?: string;
  }
> = {
  pending: {
    borderColor: 'border-gray-300',
    icon: <Clock className="w-4 h-4 text-gray-400" />,
  },
  running: {
    borderColor: 'border-yellow-400',
    icon: <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />,
    animation: 'animate-pulse',
  },
  completed: {
    borderColor: 'border-green-500',
    icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  },
  failed: {
    borderColor: 'border-red-500',
    icon: <XCircle className="w-4 h-4 text-red-500" />,
  },
  skipped: {
    borderColor: 'border-gray-400',
    icon: <SkipForward className="w-4 h-4 text-gray-400" />,
  },
};

export interface BaseNodeProps {
  id: string;
  data: NodeData;
  type: NodeType;
  selected?: boolean;
  hasSourceHandle?: boolean;
  hasTargetHandle?: boolean;
  children?: React.ReactNode;
}

export const BaseNode: React.FC<BaseNodeProps> = ({
  id,
  data,
  type,
  selected,
  hasSourceHandle = true,
  hasTargetHandle = true,
  children,
}) => {
  const config = nodeConfig[type];
  // Use nodeKey for execution status lookup (nodeKey is stored in data)
  const nodeKey = (typeof data.nodeKey === 'string' && data.nodeKey) || id.replace(/^rf-/, '');
  const executionStatus = useNodeExecutionStatus(nodeKey);
  const statusConfig = executionStatus ? executionStatusConfig[executionStatus] : null;

  // Determine border color: execution status takes precedence
  const borderColorClass = statusConfig?.borderColor || config.borderColor;

  return (
    <div
      className={clsx(
        'rounded-lg border-2 shadow-sm min-w-[180px] transition-all',
        config.bgColor,
        borderColorClass,
        selected && 'ring-2 ring-offset-2 ring-blue-500',
        statusConfig?.animation
      )}
    >
      {/* Target Handle (input) */}
      {hasTargetHandle && (
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 !bg-gray-400 border-2 border-white"
        />
      )}

      {/* Header */}
      <div
        className={clsx(
          'flex items-center gap-2 px-3 py-2 border-b',
          borderColorClass
        )}
      >
        <span className={config.color}>{config.icon}</span>
        <span className={clsx('font-medium text-sm', config.color)}>
          {data.label || config.label}
        </span>
        {/* Execution status indicator */}
        {statusConfig && (
          <span className="ml-auto">{statusConfig.icon}</span>
        )}
      </div>

      {/* Content */}
      <div className="px-3 py-2 text-xs text-gray-600">{children}</div>

      {/* Source Handle (output) */}
      {hasSourceHandle && (
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 !bg-gray-400 border-2 border-white"
        />
      )}
    </div>
  );
};

export default BaseNode;
